import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types (SQLite) ───

export interface InvoiceRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  consultation_id: string | null;
  queue_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  invoice_no: string | null;
  patient_name: string | null;
  date_iso: string | null;
  status: string | null; // 'draft' | 'issued' | 'paid'
  reference_no: string | null;
  reference_type: string | null; // 'official' | 'unofficial'
  notes: string | null;
  subtotal: number | null;
  discount_total: number | null;
  tax_total: number | null;
  grand_total: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InvoiceItemRow {
  id: string;
  org_id: string | null;
  invoice_id: string | null;
  service_name: string | null;
  service_id: string | null;
  qty: number | null;
  unit_price: number | null;
  discount: number | null;
  tax_rate: number | null;
  // PRD.190 — optional Medicine inventory reference + snapshots
  inventory_item_id: string | null;
  free_text_name: string | null;
  description_snapshot: string | null;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  unit_price_snapshot: number | null;
  tax_inclusive_snapshot: number | null; // 0/1
  open_pricing_snapshot: number | null; // 0/1
  source_type: string | null; // 'manual' | 'inventory_item' | 'prescription' | 'pharmacy_dispense' | 'procedure' | 'encounter'
  source_id: string | null;
  remarks: string | null;
  add_to_treatment: number | null; // 0/1
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InvoicePaymentRow {
  id: string;
  org_id: string | null;
  invoice_id: string | null;
  amount_cents: number | null;
  method: string | null;
  notes: string | null;
  paid_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

// ─── Queries ───

export function qInvoicesByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoices', 'by-patient', patientId] as const,
    sql: `SELECT * FROM invoices WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['invoices'],
  };
}

export function qInvoicesByEncounter(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['invoices', 'by-encounter', patientId, encounterId] as const,
    sql: `SELECT * FROM invoices WHERE patient_id = ? AND encounter_id = ? ORDER BY created_at DESC`,
    params: [patientId, encounterId],
    watch: ['invoices'],
  };
}

// ─── Outstanding balance for a single patient ───

export interface PatientOutstandingRow {
  total_balance: number;
  unpaid_count: number;
  has_overdue: number; // 1 if any invoice > 30 days old
}

export function qPatientOutstandingBalance(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoices', 'outstanding', patientId] as const,
    sql: `SELECT
      COALESCE(SUM(grand_total), 0) AS total_balance,
      COUNT(*) AS unpaid_count,
      MAX(CASE WHEN julianday('now') - julianday(created_at) > 30 THEN 1 ELSE 0 END) AS has_overdue
    FROM invoices
    WHERE patient_id = ? AND status IN ('draft', 'issued')`,
    params: [patientId],
    watch: ['invoices'],
  };
}

export function qInvoiceItems(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['invoice_items', 'by-invoice', invoiceId] as const,
    sql: `SELECT * FROM invoice_items WHERE invoice_id = ? ORDER BY sort_order ASC, created_at ASC`,
    params: [invoiceId],
    watch: ['invoice_items'],
  };
}
